import React, { useState, useEffect } from 'react';
import { 
  X, AlertTriangle, Link2, Calendar, User, Users, Building2, 
  Flag, Clock, Shield, Target, MessageCircle, Briefcase,
  ChevronRight, ExternalLink
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import CommentsPanel from './CommentsPanel';

/**
 * TrackingItemDetailDrawer - Detail view for Issues, Dependencies, Decisions
 * Shows read-only details with a Comments tab
 */
const TrackingItemDetailDrawer = ({ 
  isOpen, 
  onClose, 
  item, 
  itemType, // 'issue' | 'risk' | 'dependency' | 'decision'
  tenantId
}) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('details');
  
  if (!isOpen || !item) return null;

  const effectiveTenantId = tenantId || user?.tenant?.id || user?.tenant_id;

  const getEntityType = () => {
    if (itemType === 'issue' || itemType === 'risk') return 'issue';
    if (itemType === 'dependency') return 'dependency';
    if (itemType === 'decision') return 'decision';
    return itemType;
  };

  const tabs = [
    { id: 'details', label: 'Details' },
    { id: 'comments', label: 'Comments', icon: MessageCircle }
  ];

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-green-100 text-green-700',
      medium: 'bg-amber-100 text-amber-700',
      high: 'bg-orange-100 text-orange-700',
      critical: 'bg-red-100 text-red-700'
    };
    return colors[priority?.toLowerCase()] || 'bg-gray-100 text-gray-600';
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-amber-100 text-amber-700',
      closed: 'bg-green-100 text-green-700',
      resolved: 'bg-green-100 text-green-700',
      mitigated: 'bg-green-100 text-green-700',
      pending: 'bg-amber-100 text-amber-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700'
    };
    return colors[status?.toLowerCase()?.replace(' ', '_')] || 'bg-gray-100 text-gray-600';
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  };

  const getTitle = () => {
    if (itemType === 'issue' || itemType === 'risk') {
      return item.title || item.name;
    }
    if (itemType === 'dependency') {
      return item.name || item.description;
    }
    if (itemType === 'decision') {
      return item.title || item.name;
    }
    return item.title || item.name || 'Untitled';
  };

  const getItemKey = () => {
    if (item.issue_key) return item.issue_key;
    if (item.dependency_key) return item.dependency_key;
    if (item.decision_key) return item.decision_key;
    return item.id?.slice(-6)?.toUpperCase();
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40" 
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-xl bg-white shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-[var(--clr-surface-subtle)]">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${
              itemType === 'issue' || itemType === 'risk' ? 'bg-red-100' :
              itemType === 'dependency' ? 'bg-purple-100' : 'bg-amber-100'
            }`}>
              {(itemType === 'issue' || itemType === 'risk') && <AlertTriangle className="w-5 h-5 text-red-600" />}
              {itemType === 'dependency' && <Link2 className="w-5 h-5 text-purple-600" />}
              {itemType === 'decision' && <Target className="w-5 h-5 text-amber-600" />}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono text-gray-500">{getItemKey()}</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${getStatusColor(item.status)}`}>
                  {item.status?.replace('_', ' ') || 'Unknown'}
                </span>
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mt-0.5">{getTitle()}</h2>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id 
                  ? 'border-[var(--clr-brand-primary)] text-[var(--clr-brand-primary)]'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.icon && <tab.icon className="w-4 h-4" />}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'details' && (
            <div className="space-y-6">
              {/* Description */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Description</h3>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">
                  {item.description || 'No description provided'}
                </p>
              </div>

              {/* Key Info Grid */}
              <div className="grid grid-cols-2 gap-4">
                {item.priority && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <Flag className="w-3 h-3" /> Priority
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${getPriorityColor(item.priority)}`}>
                      {item.priority}
                    </span>
                  </div>
                )}

                {(item.raised_date || item.created_at) && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> Raised Date
                    </div>
                    <span className="text-sm font-medium">{formatDate(item.raised_date || item.created_at)}</span>
                  </div>
                )}

                {item.due_date && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Due Date
                    </div>
                    <span className="text-sm font-medium">{formatDate(item.due_date)}</span>
                  </div>
                )}

                {item.owner && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <User className="w-3 h-3" /> Owner
                    </div>
                    <span className="text-sm font-medium">{item.owner}</span>
                  </div>
                )}

                {item.escalation_level && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <Shield className="w-3 h-3" /> Escalation Level
                    </div>
                    <span className="text-sm font-medium">{item.escalation_level}</span>
                  </div>
                )}

                {item.risk_score !== undefined && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Risk Score
                    </div>
                    <span className="text-sm font-medium">{item.risk_score}</span>
                  </div>
                )}

                {item.dependency_type && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <Link2 className="w-3 h-3" /> Dependency Type
                    </div>
                    <span className="text-sm font-medium capitalize">{item.dependency_type?.replace('_', ' ')}</span>
                  </div>
                )}

                {item.decision_type && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <Target className="w-3 h-3" /> Decision Type
                    </div>
                    <span className="text-sm font-medium capitalize">{item.decision_type}</span>
                  </div>
                )}
              </div>

              {/* Resolution/Outcome */}
              {(item.resolution || item.outcome) && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">
                    {item.resolution ? 'Resolution' : 'Outcome'}
                  </h3>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">
                    {item.resolution || item.outcome}
                  </p>
                </div>
              )}

              {/* Linked Entities */}
              {item.links && item.links.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Linked Entities</h3>
                  <div className="space-y-2">
                    {item.links.map((link, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg"
                      >
                        <Briefcase className="w-4 h-4 text-gray-400" />
                        <span className="text-sm capitalize">{link.entity_type}</span>
                        <ChevronRight className="w-4 h-4 text-gray-300" />
                        <span className="text-sm text-gray-600 font-mono text-xs">
                          {link.entity_id?.slice(-8)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'comments' && (
            <CommentsPanel
              entityType={getEntityType()}
              entityId={item.id}
              tenantId={effectiveTenantId}
              compact={false}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default TrackingItemDetailDrawer;
